import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-[#F7F7F8] text-[#5E636E] border-[#D1D5DB]",
  earned: "bg-[#E8F5EC] text-[#0B6623] border-[#0B6623]",
  paid: "bg-[#EAF1FA] text-[#003366] border-[#003366]",
  reversed: "bg-[#FBEAEA] text-[#A30414] border-[#A30414]",
};

export default function Affiliates() {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [open, setOpen] = useState(null); // detail payload {affiliate, stats, commissions, payouts}

  const load = () => api.get("/admin/affiliates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = async (aff) => {
    const { data } = await api.get(`/admin/affiliates/${aff.id}/detail`);
    setOpen(data);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Growth</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Affiliate Program</h1>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary" style={{ background: "#003366" }} data-testid="new-affiliate-btn">+ New affiliate</button>
      </div>

      <div className="mt-6 border border-[#D1D5DB] overflow-x-auto">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2">Code</th>
              <th className="text-left px-4 py-2">Affiliate</th>
              <th className="text-left px-4 py-2">Discount</th>
              <th className="text-left px-4 py-2">Commission</th>
              <th className="text-left px-4 py-2">Clicks</th>
              <th className="text-left px-4 py-2">Orders</th>
              <th className="text-left px-4 py-2">Owed</th>
              <th className="text-left px-4 py-2">Paid</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={10} className="px-4 py-6 text-center font-mono text-xs text-[#5E636E]">No affiliates yet — create the first one.</td></tr>}
            {items.map((a) => (
              <tr key={a.id} data-testid={`affiliate-row-${a.code}`}>
                <td className="px-4 py-3 font-mono font-semibold">{a.code}</td>
                <td className="px-4 py-3 text-xs">{a.name}<div className="text-[#5E636E]">{a.user_email}</div></td>
                <td className="px-4 py-3 font-mono">{a.discount_pct}%</td>
                <td className="px-4 py-3 font-mono">{a.commission_pct}%</td>
                <td className="px-4 py-3 font-mono">{a.stats?.clicks ?? 0}</td>
                <td className="px-4 py-3 font-mono">{a.stats?.orders ?? 0}</td>
                <td className="px-4 py-3 font-mono font-semibold text-[#0B6623]">${(a.stats?.earned_usd ?? 0).toFixed(2)}</td>
                <td className="px-4 py-3 font-mono">${(a.stats?.paid_usd ?? 0).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 border font-mono text-[10px] uppercase ${a.status === "active" ? "bg-[#E8F5EC] text-[#0B6623] border-[#0B6623]" : "bg-[#FBEAEA] text-[#A30414] border-[#A30414]"}`}>{a.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openDetail(a)} className="text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline" data-testid={`open-affiliate-${a.code}`}>Manage</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {open && <DetailDrawer data={open} onClose={() => setOpen(null)} onChanged={async () => { await load(); openDetail(open.affiliate); }} />}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [f, setF] = useState({ user_email: "", code: "", discount_pct: 10, commission_pct: 10, name: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.user_email || !f.code) { toast.error("Email and code are required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/affiliates", { ...f, discount_pct: Number(f.discount_pct), commission_pct: Number(f.commission_pct) });
      toast.success(`Affiliate ${f.code.toUpperCase()} created`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-[#D1D5DB] p-6" onClick={(e) => e.stopPropagation()} data-testid="create-affiliate-modal">
        <div className="label-eyebrow">New affiliate</div>
        <h2 className="text-lg font-bold mt-1">Create affiliate code</h2>
        <div className="mt-4 space-y-3">
          <F label="Registered user email" v={f.user_email} on={(v) => setF({ ...f, user_email: v })} test="aff-email" placeholder="partner@lab.org" />
          <F label="Code" v={f.code} on={(v) => setF({ ...f, code: v.toUpperCase() })} test="aff-code" placeholder="LABPARTNER10" />
          <div className="grid grid-cols-2 gap-3">
            <F label="Buyer discount %" v={f.discount_pct} on={(v) => setF({ ...f, discount_pct: v })} test="aff-discount" type="number" />
            <F label="Commission %" v={f.commission_pct} on={(v) => setF({ ...f, commission_pct: v })} test="aff-commission" type="number" />
          </div>
          <F label="Display name (optional)" v={f.name} on={(v) => setF({ ...f, name: v })} test="aff-name" />
          <F label="Notes (optional)" v={f.notes} on={(v) => setF({ ...f, notes: v })} test="aff-notes" />
        </div>
        <div className="mt-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-[#D1D5DB] text-sm" data-testid="aff-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" style={{ background: "#003366" }} data-testid="aff-create-submit">{busy ? "Creating…" : "Create affiliate"}</button>
        </div>
      </div>
    </div>
  );
}

function DetailDrawer({ data, onClose, onChanged }) {
  const { affiliate, stats, commissions, payouts } = data;
  const [rates, setRates] = useState({ discount_pct: affiliate.discount_pct, commission_pct: affiliate.commission_pct });
  const [payout, setPayout] = useState({ method: "crypto", reference: "" });
  const [busy, setBusy] = useState(false);

  const saveRates = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/affiliates/${affiliate.id}`, { discount_pct: Number(rates.discount_pct), commission_pct: Number(rates.commission_pct) });
      toast.success("Rates updated");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const toggleStatus = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/admin/affiliates/${affiliate.id}`, { status: affiliate.status === "active" ? "paused" : "active" });
      toast.success(affiliate.status === "active" ? "Code paused" : "Code re-activated");
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const recordPayout = async () => {
    setBusy(true);
    try {
      const { data: p } = await api.post(`/admin/affiliates/${affiliate.id}/payouts`, payout);
      toast.success(`Payout of $${p.amount_usd.toFixed(2)} recorded`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payout failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-white border-l border-[#D1D5DB] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="affiliate-drawer">
        <div className="px-6 py-4 border-b border-[#D1D5DB] flex items-start justify-between">
          <div>
            <div className="label-eyebrow">Affiliate</div>
            <div className="font-mono text-lg font-semibold mt-1">{affiliate.code}</div>
            <div className="text-xs text-[#5E636E]">{affiliate.name} · {affiliate.user_email}</div>
          </div>
          <button onClick={toggleStatus} disabled={busy} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border disabled:opacity-50 ${affiliate.status === "active" ? "border-[#A30414] text-[#A30414]" : "border-[#0B6623] text-[#0B6623]"}`} data-testid="toggle-affiliate-status">
            {affiliate.status === "active" ? "Pause code" : "Re-activate"}
          </button>
        </div>
        <div className="px-6 py-4 space-y-6 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Clicks" value={stats.clicks} />
            <Kpi label="Orders" value={stats.orders} />
            <Kpi label="Owed (earned)" value={`$${stats.earned_usd.toFixed(2)}`} strong />
            <Kpi label="Paid out" value={`$${stats.paid_usd.toFixed(2)}`} />
          </div>

          <div>
            <div className="label-eyebrow mb-2">Rates</div>
            <div className="flex items-end gap-3 flex-wrap">
              <F label="Buyer discount %" v={rates.discount_pct} on={(v) => setRates({ ...rates, discount_pct: v })} test="edit-discount" type="number" small />
              <F label="Commission %" v={rates.commission_pct} on={(v) => setRates({ ...rates, commission_pct: v })} test="edit-commission" type="number" small />
              <button onClick={saveRates} disabled={busy} className="btn-primary" style={{ background: "#003366" }} data-testid="save-rates">Save rates</button>
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-2">Record payout</div>
            {stats.earned_usd > 0 ? (
              <div className="flex items-end gap-3 flex-wrap bg-[#F7F7F8] border border-[#D1D5DB] p-3">
                <div className="font-mono text-sm font-semibold text-[#0B6623]" data-testid="payout-amount">${stats.earned_usd.toFixed(2)}</div>
                <div>
                  <label className="label-eyebrow block mb-1">Method</label>
                  <select value={payout.method} onChange={(e) => setPayout({ ...payout, method: e.target.value })} className="border border-[#D1D5DB] bg-white px-2 py-1.5 text-sm" data-testid="payout-method">
                    {["crypto", "paypal", "bank", "other"].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <F label="Reference / tx (optional)" v={payout.reference} on={(v) => setPayout({ ...payout, reference: v })} test="payout-reference" small />
                <button onClick={recordPayout} disabled={busy} className="btn-primary" style={{ background: "#0B6623" }} data-testid="record-payout">Mark paid</button>
              </div>
            ) : (
              <div className="font-mono text-xs text-[#5E636E]">No earned balance to pay out.</div>
            )}
          </div>

          <div>
            <div className="label-eyebrow mb-2">Commissions ({commissions.length})</div>
            <table className="w-full data-table">
              <thead><tr><th className="text-left px-2 py-1">Order</th><th className="text-left px-2 py-1">Buyer</th><th className="text-right px-2 py-1">Commission</th><th className="text-left px-2 py-1">Status</th></tr></thead>
              <tbody>
                {commissions.length === 0 && <tr><td colSpan={4} className="px-2 py-3 text-center font-mono text-xs text-[#5E636E]">No referred orders yet.</td></tr>}
                {commissions.map((c) => (
                  <tr key={c.id}>
                    <td className="px-2 py-1.5 font-mono text-xs">{c.order_number}</td>
                    <td className="px-2 py-1.5 text-xs">{c.buyer_email}</td>
                    <td className="px-2 py-1.5 font-mono text-xs text-right">${Number(c.commission_usd || 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5"><span className={`inline-block px-1.5 py-0.5 border font-mono text-[10px] uppercase ${STATUS_COLORS[c.status] || ""}`}>{c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <div className="label-eyebrow mb-2">Payouts ({payouts.length})</div>
            <table className="w-full data-table">
              <thead><tr><th className="text-left px-2 py-1">Date</th><th className="text-right px-2 py-1">Amount</th><th className="text-left px-2 py-1">Method</th><th className="text-left px-2 py-1">Reference</th></tr></thead>
              <tbody>
                {payouts.length === 0 && <tr><td colSpan={4} className="px-2 py-3 text-center font-mono text-xs text-[#5E636E]">No payouts recorded.</td></tr>}
                {payouts.map((p) => (
                  <tr key={p.id}>
                    <td className="px-2 py-1.5 font-mono text-xs">{p.created_at?.slice(0, 10)}</td>
                    <td className="px-2 py-1.5 font-mono text-xs text-right font-semibold">${Number(p.amount_usd || 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5 font-mono text-xs uppercase">{p.method}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">{p.reference || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, strong }) => (
  <div className="border border-[#D1D5DB] bg-[#F7F7F8] p-3">
    <div className={`font-mono text-lg ${strong ? "font-bold text-[#0B6623]" : "font-semibold"}`}>{value}</div>
    <div className="label-eyebrow mt-0.5">{label}</div>
  </div>
);

function F({ label, v, on, test, type = "text", placeholder, small }) {
  return (
    <div className={small ? "w-36" : ""}>
      <label className="label-eyebrow block mb-1">{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} className="w-full border border-[#D1D5DB] bg-white px-2 py-1.5 text-sm" data-testid={test} />
    </div>
  );
}
